import React, { useEffect, useState, ReactNode } from 'react';
import { AnimationVariant } from '@/services/enums';

interface StaggeredAnimationProps {
  children: ReactNode[];
  delay?: number; // 每个元素之间的延迟时间（毫秒）
  duration?: number; // 动画持续时间（毫秒）
  animation?: AnimationVariant;
  className?: string;
}

const StaggeredAnimation: React.FC<StaggeredAnimationProps> = ({
  children,
  delay = 200,
  duration = 600,
  animation = 'fadeInUp',
  className = '',
}) => {
  const [visibleItems, setVisibleItems] = useState<boolean[]>([]);

  useEffect(() => {
    // 初始化所有元素为不可见
    setVisibleItems(new Array(children.length).fill(false));

    // 依次显示每个元素
    children.forEach((_, index) => {
      setTimeout(() => {
        setVisibleItems((prev) => {
          const newState = [...prev];
          newState[index] = true;
          return newState;
        });
      }, index * delay);
    });
  }, [children, delay]);

  const getAnimationClass = (isVisible: boolean) => {
    const baseClass = 'transition-all duration-600 ease-out';

    if (!isVisible) {
      switch (animation) {
        case 'fadeInUp':
          return `${baseClass} opacity-0 translate-y-8`;
        case 'fadeInDown':
          return `${baseClass} opacity-0 -translate-y-8`;
        case 'fadeInLeft':
          return `${baseClass} opacity-0 -translate-x-8`;
        case 'fadeInRight':
          return `${baseClass} opacity-0 translate-x-8`;
        case 'scaleIn':
          return `${baseClass} opacity-0 scale-95`;
        case 'slideIn':
          return `${baseClass} opacity-0 translate-y-4`;
        default:
          return `${baseClass} opacity-0 translate-y-8`;
      }
    }

    switch (animation) {
      case 'fadeInUp':
        return `${baseClass} opacity-100 translate-y-0`;
      case 'fadeInDown':
        return `${baseClass} opacity-100 translate-y-0`;
      case 'fadeInLeft':
        return `${baseClass} opacity-100 translate-x-0`;
      case 'fadeInRight':
        return `${baseClass} opacity-100 translate-x-0`;
      case 'scaleIn':
        return `${baseClass} opacity-100 scale-100`;
      case 'slideIn':
        return `${baseClass} opacity-100 translate-y-0`;
      default:
        return `${baseClass} opacity-100 translate-y-0`;
    }
  };

  return (
    <div className={className}>
      {children.map((child, index) => (
        <div
          key={index}
          className={getAnimationClass(visibleItems[index])}
          style={{
            transitionDelay: `${index * delay}ms`,
            transitionDuration: `${duration}ms`,
          }}
        >
          {child}
        </div>
      ))}
    </div>
  );
};

export default StaggeredAnimation;
